import { AtoN, Pss } from '@ais-ui/api-spec/config';
import { useLocalization } from '@ais-ui/locale-management';
import { useFetchPSSWithType } from '@ais-ui/api';

import React, { useEffect, useState } from 'react';
import { AccordionItem, AccordionTrigger, Separator } from '@ais-ui/ui-core';

import { cn } from '@ais-ui/utils';

import {
  DisabledShowAtonButton,
  EnabledShowAtonButton,
} from './ShowAtonButton';
import { AtonItemContent } from './AtonItemContent';
import { useDateTimeFormat } from '../../conversions/useDateFormat';
import { GhVirtualAton } from '../../../assets/icons/GhVirtualAton';
import {
  DisabledVirtualDropdownMenu,
  EnabledVirtualDropdownMenu,
} from './VirtualDropdownMenu';

interface VirtualAtonItemProps {
  aton: AtoN;
  requiresMap: boolean;
}

interface AtonItemContentProps {
  aton: AtoN;
}

const VirtualAtonItemContent = ({ aton }: AtonItemContentProps) => {
  const { messages } = useLocalization();
  const { data: allPss } = useFetchPSSWithType('aton');
  const { formatDateWithTimeZone, formatTimeWithTimeZone } =
    useDateTimeFormat();

  const [pssNames, setPssNames] = useState<string>('');
  const [chainedPssName, setChainedPssName] = useState<string>('');

  useEffect(() => {
    if (!allPss || !allPss.records) {
      return;
    }

    const newPssNames = aton.pss
      ? aton.pss
          .map(
            (pssId) =>
              (allPss.records as Pss[]).find((pss) => pss.uuid === pssId)?.name
          )
          .filter((name) => name != null)
          .join(', ')
      : messages.atonVirtualItemNoPss;

    setPssNames(newPssNames);
  }, [allPss, aton.pss]);

  useEffect(() => {
    if (!allPss || !allPss.records) {
      return;
    }

    const chainedAtonName = allPss.records.find(
      (pss) => pss.uuid === aton.chainedaton
    )?.name;

    setChainedPssName(
      chainedAtonName ? chainedAtonName : messages.atonVirtualItemNoChainedAton
    );
  }, [allPss, aton.chainedaton]);

  return (
    <>
      <div className="flex px-4 space-x-2">
        <p className="text-muted-foreground">
          {messages.atonVirtualItemPssLabel}
        </p>
        <p>{pssNames}</p>
      </div>
      <div className="flex px-4 space-x-2">
        <p className="text-muted-foreground">
          {messages.atonVirtualItemChainedPssLabel}
        </p>
        <p>{chainedPssName}</p>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="flex px-4 space-x-2">
          <p className="text-muted-foreground">
            {messages.atonVirtualItemSizeALabel}
          </p>
          <p>{aton.sizeA}</p>
        </div>
        <div className="flex px-4 space-x-2">
          <p className="text-muted-foreground">
            {messages.atonVirtualItemSizeBLabel}
          </p>
          <p>{aton.sizeB}</p>
        </div>
        <div className="flex px-4 space-x-2">
          <p className="text-muted-foreground">
            {messages.atonVirtualItemSizeCLabel}
          </p>
          <p>{aton.sizeC}</p>
        </div>
        <div className="flex px-4 space-x-2">
          <p className="text-muted-foreground">
            {messages.atonVirtualItemSizeDLabel}
          </p>
          <p>{aton.sizeD}</p>
        </div>
      </div>

      <div className="flex flex-col pb-4 px-4 space-x-2 space-y-2">
        <p className="text-muted-foreground pb-2">
          {messages.atonVirtualItemScheduleLabel}
        </p>
        {aton?.schedule && aton.schedule.length > 0 ? (
          aton.schedule.map((schedule, index) => (
            <div key={index} className="flex justify-between items-center">
              <div className="flex flex-col items-end">
                <div>{formatDateWithTimeZone(schedule.start)}</div>
                <div className="text-muted-foreground">
                  {formatTimeWithTimeZone(schedule.start)}
                </div>
              </div>
              <p className="text-muted-foreground">to</p>
              <div className="flex flex-col items-end">
                <div>{formatDateWithTimeZone(schedule.end)}</div>
                <div className="text-muted-foreground">
                  {formatTimeWithTimeZone(schedule.end)}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div>{messages.atonVirtualItemNoScheduleValue}</div>
        )}
      </div>
    </>
  );
};

export const VirtualAtonItem = ({
  aton,
  requiresMap,
}: VirtualAtonItemProps) => {
  return (
    <AccordionItem
      key={aton.uuid}
      value={aton.uuid as string}
      className={cn('pr-4 pl-2')}
    >
      <div className="flex justify-between items-center">
        <div className="flex items-center">
          <div className="flex space-x-1 items-center">
            {requiresMap && (
              <EnabledShowAtonButton aton={aton}></EnabledShowAtonButton>
            )}
            {!requiresMap && <DisabledShowAtonButton></DisabledShowAtonButton>}
            <Separator orientation="vertical" className="h-6 bg-gray-300" />
          </div>

          <AccordionTrigger className="w-[205px]" hideChevron={true}>
            <div className="flex ml-4 space-x-2 items-center">
              <GhVirtualAton className="h-4 w-4" />
              <div className="ml-4 space-y-1">
                <p className="text-sm text-left font-medium leading-none group-hover:underline">
                  {aton.name}
                </p>
              </div>
            </div>
          </AccordionTrigger>
        </div>
        {requiresMap && <EnabledVirtualDropdownMenu aton={aton} />}
        {!requiresMap && (
          <DisabledVirtualDropdownMenu
            aton={aton}
          ></DisabledVirtualDropdownMenu>
        )}
      </div>

      <AtonItemContent aton={aton}>
        <VirtualAtonItemContent aton={aton}></VirtualAtonItemContent>
      </AtonItemContent>
    </AccordionItem>
  );
};
